import { Alert } from '@mui/material';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import Tabs from '../../../../components/Tabs';
import { useLanguage } from '../../../../contexts/language';
import { VesselTemplateSubPageContext } from '../types';
import CertificationTabContent from './CertificationTabContent';
import useFreshDataEnsurer from '../../../../hooks/useFreshDataEnsurer';
import useDraftList from '../../../../hooks/useDraftList';
import { useNavigationGuardBlocker } from '../../../../hooks/useNavigationGuard';
import { VesselCertificateStatus } from '../../../../types/core';

const VesselCertificationsPage = () => {
  const { vessel, refetchVessel, certificatesManagement } =
    useOutletContext<VesselTemplateSubPageContext>();
  const {
    vesselCertificates,
    refetch: refetchCertificates,
    isFetching: isFetchingCertificates,
  } = certificatesManagement;
  const { t } = useLanguage();

  const areCertificatesFresh = useFreshDataEnsurer(
    isFetchingCertificates,
    refetchCertificates,
  );

  const { list, isReady, operations } = useDraftList(
    vesselCertificates,
    areCertificatesFresh,
  );

  const pendingCertifications = useMemo(
    () =>
      list.filter(
        item =>
          !item.isDraft &&
          item.data.status === VesselCertificateStatus.PendingApproval,
      ),
    [list],
  );

  useNavigationGuardBlocker(() => Boolean(list.find(item => item.isDraft)));

  return (
    <Tabs
      tabs={[
        { label: t('content.all') },
        { label: t('content.pending-approval') },
      ]}
      variant="buttons"
    >
      <CertificationTabContent
        vessel={vessel}
        draftListManager={{ list, isReady, operations }}
        emptyContent={
          <Alert severity="error" className="mb-4 shadow-iots">
            {t('validation.valid-certifications-minimum-button')}
          </Alert>
        }
        showAddButton
        areCertificatesPending={!areCertificatesFresh}
        isFetchingCertificates={isFetchingCertificates}
        onCertificatesDataUpdate={() => {
          refetchVessel();
          refetchCertificates();
        }}
      />
      <CertificationTabContent
        vessel={vessel}
        draftListManager={{ list: pendingCertifications, isReady, operations }}
        emptyContent={
          <div className="grid place-items-center w-full grow">
            <h1 className="text-2xl font-bold">
              {t('content.no-certifications-found')}
            </h1>
          </div>
        }
        areCertificatesPending={!areCertificatesFresh}
        isFetchingCertificates={isFetchingCertificates}
        onCertificatesDataUpdate={() => {
          refetchVessel();
          refetchCertificates();
        }}
      />
    </Tabs>
  );
};

export default VesselCertificationsPage;
